import React, { useMemo } from "react";
import styled from "styled-components";
import { Trans, useTranslation } from "@coworker/locales";
import { CustomArticleWidget } from "./CustomArticleWidget";
import {
  useCountryId,
  useFixaUID,
  useStoreId,
  useUID,
} from "../../../core/auth/useLoggedInUser";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import { CreateTestBuy, TestBuyProduct } from "@coworker/types/lib/testbuy";
import dayjs from "dayjs";
import { ARTICLE_HASH } from "../constants";
import profilePreferences from "@coworker/enums/profilePreferences";
import { useUserPreference } from "../../../hooks/useProfilePreferencesQuery";
import StaticFilterRow from "../TestBuyDetailPage/StaticFilterRow";
import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import Button from "@coworker/components/src/components/Button";
import { postToTestbuyService } from "../../../hooks/API/testbuy.service";
import { useMyStore } from "@coworker/app/src/core/auth/useLoggedInUser";
import FullScreenPopup from "@coworker/apprestructured/src/layout/components/FullScreenPopup/FullScreenPopup";
import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";
import { useParams } from "react-router";
import { CustomTestbuyItemsListViewItem } from "@coworker/apprestructured/src/testbuy/components/CustomTestbuyItemsListViewItem/CustomTestbuyItemsListViewItem";
import { FixaWindow } from "@coworker/reusable/helpers/devices";

const MaxNumberOfArticles = styled("div")`
  text-align: center;
  margin-top: 1rem;
`;

const FilterWrapper = styled("div")`
  width: 100%;
`;

const NumberOfArticlesHeader = styled("h3")`
  padding: 1rem 0;
  border-bottom: 1px solid var(--grey150);
  width: 100%;
  font-size: 18px;
`;

const ArticlesLengthWrapper = styled("div")`
  margin-left: 2rem;
`;

export const CustomTestBuyForm = () => {
  const { enableUIDInTestbuy } = window as FixaWindow;

  const UID = useUID();
  const fixaUid = useFixaUID();
  const uid = enableUIDInTestbuy ? UID : fixaUid;

  const [articles, setArticles] = React.useState<TestBuyProduct[]>([]);
  const storeId: string = useStoreId();
  const navigate = useFixaNavigate();
  const { showToast } = useToastNotification();
  const countryId = useCountryId();
  const { t } = useTranslation();

  const { location } = useParams();

  const store = useMyStore();
  const storeLocale = store?.primaryLocale || "";
  const [userLanguage] = useUserPreference(
    profilePreferences.LANGUAGE,
    storeLocale
  );
  const userOrStoreLanguage = userLanguage || storeLocale;
  const onSubmit = async () => {
    const createTestbuyBody = {
      numberOfArticles: articles?.length,
      productIds: articles?.map((article) => article.shortId),
      area: location,
      storeId,
      includeOutgoing: true,
      includeOutOfStock: true,
      createdAt: dayjs().format("YYYY-MM-DD"),
      createdBy: uid,
      typeOfArticles: ARTICLE_HASH.CUSTOM?.type,
      divisionId: "",
      area_new: [location],
      countryId,
      locale: userOrStoreLanguage ?? "en_GB",
    };
    try {
      if (!location) return;
      await postToTestbuyService(
        "testbuy/create_testbuy",
        createTestbuyBody,
        (data: CreateTestBuy) => {
          if (data.testBuyId) {
            navigate(`/testbuy/${data.testBuyId}`);
          } else {
            const message =
              data?.message === "INSUFFICIENT_ARTICLES" ? (
                <Trans>insufficientArticles</Trans>
              ) : (
                <Trans>searchErrorString</Trans>
              );
            showToast(message, undefined, undefined, 5);
          }
        }
      );
    } catch (error) {
      const message =
        error instanceof Error && error.message === "INSUFFICIENT_ARTICLES" ? (
          <Trans>insufficientArticles</Trans>
        ) : (
          <Trans>searchErrorString</Trans>
        );
      showToast(message, undefined, undefined, 5);
    }
  };

  const linkbarItems = useMemo(() => {
    return [
      {
        label: t("testBuysString"),
        to: "/testbuy",
      },
      {
        label: t("newTestBuy"),
        to: "/testbuy/new",
      },
    ];
  }, [t]);

  return (
    <FullScreenPopup
      noPadding
      actionBarContent={
        <Button
          primary
          dark
          text={<Trans>createTestbuyString</Trans>}
          disabled={!articles.length}
          onClick={onSubmit}
        />
      }
      linkbar={
        <Linkbar
          items={linkbarItems}
          currentLabel={t("newCustomTestBuy")}
          currentAsH1
        />
      }
    >
      <FilterWrapper>
        <StaticFilterRow
          typeOfArticles={ARTICLE_HASH.CUSTOM?.type}
          area={location}
        />
      </FilterWrapper>
      <NumberOfArticlesHeader>
        <ArticlesLengthWrapper>
          <Trans>articlesAddedString</Trans>: <span>{articles.length}</span>
        </ArticlesLengthWrapper>
      </NumberOfArticlesHeader>
      {articles?.map((article) => (
        <CustomTestbuyItemsListViewItem
          itemNo={article.shortId}
          homeLocationGridCode={article.location}
        />
      ))}
      {articles?.length < 30 ? (
        <CustomArticleWidget articles={articles} setArticles={setArticles} />
      ) : (
        <MaxNumberOfArticles>
          <Trans>youCantAddMoreArticlesString</Trans>
        </MaxNumberOfArticles>
      )}
    </FullScreenPopup>
  );
};
